import { ChevronRight, Handshake } from "lucide-react";
import { fmt } from "../lib/constants";
import { HINT } from "./HowWeCount";

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-");

const Chip = ({ testId, onClick, title, muted, children }) => {
  const cls = `inline-flex items-center gap-1 rounded-full border px-2 py-0.5 tv:px-[10px] tv:py-[3px] text-[11px] tv:text-[14px] whitespace-nowrap ${
    muted ? "border-amber-200 text-amber-700/60" : "border-amber-300 bg-panel/70 text-amber-900"
  } ${onClick ? "hover:bg-amber-200/70 hover:border-amber-400 transition-colors" : ""}`;
  return onClick ? (
    <button type="button" data-testid={testId} onClick={onClick} title={title} className={cls}>{children}</button>
  ) : (
    <span data-testid={testId} title={title} className={cls}>{children}</span>
  );
};

const Divider = () => <span className="w-px h-4 tv:h-[20px] bg-amber-300 mx-1" aria-hidden="true" />;

// Partner-sourced venues per day, last 30 days
const Spark = ({ series }) => {
  const max = Math.max(1, ...series.map((d) => d.partner || 0));
  return (
    <div className="hidden sm:flex items-end gap-px h-7 tv:h-[34px]" data-testid="partners-sparkline" title={`Partner-sourced venues per day · ${series.length < 30 ? "this month" : "last 30 days"}`}>
      {series.map((d) => (
        <span
          key={d.date}
          className={`w-[3px] tv:w-[4px] rounded-sm ${d.partner ? "bg-amber-500" : "bg-amber-200/80"}`}
          style={{ height: `${d.partner ? Math.max(20, (d.partner / max) * 100) : 10}%` }}
        />
      ))}
    </div>
  );
};

const Mini = ({ label, value, testId }) => (
  <div className="min-w-0">
    <div className="eyebrow text-amber-800 whitespace-nowrap">{label}</div>
    <div data-testid={testId} className="num font-extrabold text-xl tv:text-[26px] leading-none text-amber-900 mt-0.5 tv:mt-[4px]">{fmt(value)}</div>
  </div>
);

// Strip between the KPI panel and the country table: venues won through the Partnership / Channel partner pipelines
export const PartnersStrip = ({ view, range, onOpen, onCountry }) => {
  const partners = view?.partners;
  const total = partners?.total || 0;
  const won = view?.totals?.won || 0;
  const share = won ? Math.round((total / won) * 100) : 0;
  const countries = (view?.countries || []).filter((c) => c.partner > 0).sort((a, b) => b.partner - a.partner);
  const pipelines = Object.entries(partners?.by_pipeline || {});
  const byPartner = partners?.by_partner || [];
  return (
    <section
      data-testid="partners-strip"
      className="rounded-2xl border border-amber-300/70 bg-amber-50 shadow-sm px-4 py-3 tv:px-[22px] tv:py-[10px] fade-up flex flex-col gap-2.5 tv:gap-[8px]"
    >
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 tv:gap-[22px]">
        <button type="button" data-testid="partners-open-button" onClick={onOpen} title={`${HINT.partner} Click for the partner venue list.`} className="flex items-center gap-3 tv:gap-[14px] text-left group shrink-0">
          <span className="w-10 h-10 tv:w-[52px] tv:h-[52px] rounded-full bg-amber-400/30 text-amber-800 flex items-center justify-center group-hover:bg-amber-400/50 transition-colors shrink-0">
            <Handshake className="w-5 h-5 tv:w-[26px] tv:h-[26px]" />
          </span>
          <span className="min-w-0">
            <span className="eyebrow text-amber-800 flex items-center gap-1.5 whitespace-nowrap">
              Via partners{range ? " · this period" : ""}
              <ChevronRight className="w-3 h-3 tv:w-[14px] tv:h-[14px] opacity-60 group-hover:translate-x-0.5 transition-transform" />
            </span>
            <span className="flex items-baseline gap-2 tv:gap-[10px]">
              <span data-testid="partners-total" className="num font-black text-3xl tv:text-[40px] leading-none text-amber-900">{fmt(total)}</span>
              <span data-testid="partners-share" className="num text-xs tv:text-[14px] text-amber-800/80 whitespace-nowrap">of {fmt(won)} venues · {share}%</span>
            </span>
          </span>
        </button>
        {!range && (
          <div className="flex items-center gap-4 tv:gap-[22px] shrink-0">
            <Mini label="This month" value={view?.new_clients?.this_month_partner} testId="partners-month" />
            <Mini label="Last 7 days" value={view?.new_clients?.last_7_days_partner} testId="partners-7days" />
            {view?.daily_series && <Spark series={view.daily_series} />}
          </div>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-1.5 tv:gap-[8px]" data-testid="partners-breakdown">
        {pipelines.map(([label, n]) => (
          <Chip key={label} testId={`partners-pipeline-${slug(label)}`} muted={!n} title={`Won through the ${label} pipeline`}>
            {label} <b className="num">{fmt(n)}</b>
          </Chip>
        ))}
        {countries.length > 0 && <Divider />}
        {countries.map((c) => (
          <Chip key={c.code} testId={`partners-country-${c.code}`} onClick={onCountry ? () => onCountry(c.code) : undefined} title={`${c.name} · partner-sourced venues – click for the list`}>
            <span className="font-mono text-[10px] tv:text-[12px] text-amber-700">{c.code}</span> {c.name} <b className="num">{fmt(c.partner)}</b>
          </Chip>
        ))}
        {byPartner.length > 0 && <Divider />}
        {byPartner.map((p, i) => (
          <Chip key={p.name} testId={`partners-partner-${i}`} title="Partner company (HubSpot “Partner Name” on the deal)">
            {p.name} <b className="num">{fmt(p.count)}</b>
          </Chip>
        ))}
        {!total && (
          <span className="text-xs tv:text-[14px] text-amber-800/70" data-testid="partners-empty">
            No partner-sourced venues {range ? "in this period" : "in this view"} yet
          </span>
        )}
      </div>
    </section>
  );
};
